import React, { useState, useEffect, useCallback } from "react";
import { Bell, CheckCheck, ExternalLink, Clock } from "lucide-react";
import { Link } from "react-router-dom";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import { api, asItems, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Skeleton } from "@/components/ui/skeleton";

/**
 * Notification bell + popover panel.
 * Polls /api/notifications every 30s for unread count.
 */
export default function NotificationBell({ buttonClass, dotColor = "hsl(var(--destructive))" }) {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get("/notifications?limit=20");
      setItems(asItems(data));
      setUnread(data.unread || 0);
    } catch (requestError) {
      setError(formatApiError(requestError));
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);

  // Refresh when opening
  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const markRead = async (id) => {
    try {
      await api.post(`/notifications/${id}/read`);
      setItems((prev) => prev.map((n) => n.notification_id === id ? { ...n, read: true } : n));
      setUnread((u) => Math.max(0, u - 1));
    } catch (requestError) {
      setError(formatApiError(requestError));
    }
  };
  const markAllRead = async () => {
    try {
      await api.post(`/notifications/read-all`);
      setItems((prev) => prev.map((n) => ({ ...n, read: true })));
      setUnread(0);
    } catch (requestError) {
      setError(formatApiError(requestError));
    }
  };

  if (!user) {
    return (
      <Link
        to="/login"
        className={buttonClass || "hidden lg:inline-grid place-items-center w-9 h-9 rounded-full hover:bg-muted transition relative"}
        aria-label="Log in to view notifications"
        title="Log in to view notifications"
        data-testid="util-bell"
      >
        <Bell className="w-4 h-4 text-current" />
      </Link>
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={buttonClass || "hidden lg:inline-grid place-items-center w-9 h-9 rounded-full hover:bg-muted transition relative"}
          aria-label={`Notifications${unread > 0 ? ` · ${unread} unread` : ""}`}
          data-testid="util-bell"
        >
          <Bell className="w-4 h-4 text-current" />
          {unread > 0 && (
            <span className="absolute top-2 right-2 flex h-2.5 w-2.5" data-testid="bell-unread-dot">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-rose-400 opacity-75 duration-1000" />
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-rose-500 shadow-sm" />
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[360px] p-0 overflow-hidden" sideOffset={6} data-testid="bell-panel">
        <div className="flex items-center justify-between px-4 py-3 border-b border-border">
          <div>
            <p className="font-bold text-foreground text-sm">Notifications</p>
            <p className="text-[11px] text-muted-foreground">{unread > 0 ? `${unread} unread` : "All caught up"}</p>
          </div>
          {unread > 0 && (
            <button
              type="button"
              onClick={markAllRead}
              title="Mark every notification as read"
              className="text-[11px] font-bold text-primary inline-flex items-center gap-1 hover:underline"
              data-testid="bell-mark-all-read"
            >
              <CheckCheck className="w-3 h-3" /> Mark all read
            </button>
          )}
        </div>
        <div className="max-h-[420px] overflow-y-auto divide-y divide-border">
          {!loading && error && (
            <div className="m-3 rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-xs text-destructive" role="alert">
              <p>{error}</p>
              <button type="button" onClick={load} className="mt-2 font-bold underline underline-offset-2">Retry</button>
            </div>
          )}
          {loading && (
            <div className="space-y-3 px-4 py-4" aria-label="Loading notifications">
              {[0, 1, 2].map((row) => (
                <div key={row} className="flex gap-3">
                  <Skeleton className="mt-1 h-2 w-2 rounded-full shrink-0" />
                  <div className="flex-1 space-y-2">
                    <Skeleton className="h-3 w-2/3" />
                    <Skeleton className="h-3 w-full" />
                    <Skeleton className="h-2.5 w-1/3" />
                  </div>
                </div>
              ))}
            </div>
          )}
          {!loading && !error && items.length === 0 && (
            <div className="px-4 py-10 text-center">
              <Bell className="w-6 h-6 mx-auto text-muted-foreground mb-2" />
              <p className="text-xs text-muted-foreground">No notifications yet.</p>
            </div>
          )}
          {!loading && items.map((n) => (
            <button
              type="button"
              key={n.notification_id}
              onClick={() => !n.read && markRead(n.notification_id)}
              title={n.read ? "Notification already read" : "Mark notification as read"}
              className={`w-full text-left px-4 py-3 flex gap-3 transition ${
                n.read ? "bg-background" : "bg-secondary/70 hover:bg-accent/60"
              }`}
              data-testid={`bell-item-${n.notification_id}`}
            >
              <div className="shrink-0 mt-1">
                <span className={`block w-2 h-2 rounded-full ${n.read ? "bg-muted-foreground/30" : "bg-primary"}`} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[13px] font-semibold text-foreground truncate">{n.title}</p>
                <p className="text-[12px] text-muted-foreground line-clamp-2">{n.body}</p>
                <p className="text-[10px] text-muted-foreground/80 mt-1 flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  {new Date(n.at).toLocaleString("en-IN")}
                </p>
              </div>
            </button>
          ))}
        </div>
        <div className="border-t border-border px-4 py-2.5">
          <Link
            to="/dashboard/notifications"
            onClick={() => setOpen(false)}
            className="text-[12px] font-bold text-primary inline-flex items-center gap-1 hover:underline"
            data-testid="bell-view-all"
          >
            View all <ExternalLink className="w-3 h-3" />
          </Link>
        </div>
      </PopoverContent>
    </Popover>
  );
}
